"use client";

import axios from "axios";

import { useAuthStore } from "./auth-store";
import { API_BASE_URL } from "./api-base";
import { demoDynamicForms, demoTaxonomyResults, fallbackMarket } from "./demo-data";
import type {
  AuthResult,
  AuthUser,
  ChatConversation,
  ChatMessage,
  Community,
  CommunityMember,
  Consultation,
  DemandPost,
  DemandResponse,
  DynamicFormResponse,
  FarmActivitiesEnvelope,
  FarmActivity,
  FarmActivityType,
  FarmDashboard,
  FarmRecord,
  ListingPhotoUpload,
  LocationAdminLevel,
  LocationAdminUnit,
  LocationCountry,
  LiveAccess,
  LiveAnalytics,
  LiveComment,
  LiveMember,
  LiveModerationAction,
  LivePinnedItem,
  LiveParticipant,
  LiveQuestion,
  LiveReaction,
  LiveRecording,
  LiveSpeakerRequest,
  LiveTicketStatus,
  MarketConfig,
  MyListing,
  PostComment,
  PostItem,
  PostLikeActivity,
  ProfileStats,
  ServiceRequest,
  SocialPost,
  SocialUserCard,
  StructuredAttributeValues,
  TaxonomySearchResult,
  UserNotificationPreferences,
  UserPrivacySettings,
  UserSearchResult,
} from "./types";

export { API_BASE_URL } from "./api-base";

const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
});

http.interceptors.request.use((config) => {
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let isRefreshing = false;
type RefreshQueueEntry = { resolve: (token: string) => void; reject: (err: unknown) => void };
const refreshQueue: RefreshQueueEntry[] = [];

function flushRefreshQueue(err: unknown, token: string | null) {
  for (const entry of refreshQueue) {
    if (err) entry.reject(err);
    else entry.resolve(token!);
  }
  refreshQueue.length = 0;
}

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config as typeof error.config & { _retry?: boolean };

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error);
    }

    const store = useAuthStore.getState();

    if (!store.refreshToken) {
      // Only treat this as session expiry when a session actually existed.
      // Anonymous pages may probe guarded endpoints (e.g. author lookups) and
      // must not be bounced to /auth for a plain 401.
      if (store.accessToken || store.user) {
        store.logout();
        if (typeof window !== "undefined") {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/auth?next=${next}`;
        }
      }
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise<string>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then((token) => {
        original.headers.Authorization = `Bearer ${token}`;
        return http(original);
      });
    }

    original._retry = true;
    isRefreshing = true;

    try {
      const res = await axios.post<unknown>(`${API_BASE_URL}/auth/refresh`, {
        refreshToken: store.refreshToken,
      });
      const body = res.data as Record<string, unknown>;
      const payload = (body && typeof body === "object" && "data" in body ? body.data : body) as AuthResult;
      const newToken = payload?.accessToken;
      if (!newToken) throw new Error("Refresh returned no access token.");
      store.setSession({
        accessToken: newToken,
        refreshToken: payload.refreshToken ?? store.refreshToken!,
        user: payload.user ?? store.user!,
      });
      original.headers.Authorization = `Bearer ${newToken}`;
      flushRefreshQueue(null, newToken);
      return http(original);
    } catch (refreshErr) {
      flushRefreshQueue(refreshErr, null);
      useAuthStore.getState().logout();
      if (typeof window !== "undefined") {
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/auth?next=${next}`;
      }
      return Promise.reject(refreshErr);
    } finally {
      isRefreshing = false;
    }
  },
);

function unwrap<T>(body: unknown): T {
  const record = body as { data?: unknown };
  return (record && typeof record === "object" && "data" in record ? record.data : body) as T;
}

async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await http.get(url, { params });
  return unwrap<T>(response.data);
}

async function post<T>(url: string, data?: unknown, headers?: Record<string, string>): Promise<T> {
  const response = await http.post(url, data, { headers });
  return unwrap<T>(response.data);
}

async function del<T>(url: string): Promise<T> {
  const response = await http.delete(url);
  return unwrap<T>(response.data);
}

async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await http.patch(url, data);
  return unwrap<T>(response.data);
}

export const authApi = {
  emailLogin: (email: string, password: string) =>
    post<AuthResult>("/auth/email/login", {
      email: email.trim().toLowerCase(),
      password,
    }),
  emailRegister: (dto: { email: string; password: string; displayName: string; username?: string }) =>
    post<AuthResult>("/auth/email/register", {
      ...dto,
      email: dto.email.trim().toLowerCase(),
      displayName: dto.displayName.trim(),
      username: dto.username?.trim() || undefined,
    }),
  sendOtp: (phoneNumber: string) =>
    post<{ _devOtp?: string; message?: string }>("/auth/otp/send", { phoneNumber }),
  verifyOtp: (phoneNumber: string, code: string) =>
    post<AuthResult>("/auth/otp/verify", {
      phoneNumber,
      code,
      deviceInfo: "web-client",
    }),
  socialSignIn: (provider: "google" | "apple", idToken: string, nonce?: string) =>
    post<AuthResult>("/auth/social", {
      provider,
      idToken,
      ...(nonce ? { nonce } : {}),
    }),
  requestPasswordReset: (email: string) =>
    post<{ message: string }>("/auth/password/forgot", {
      email: email.trim().toLowerCase(),
    }),
  resetPassword: (token: string, newPassword: string) =>
    post<{ reset: true; sessionsRevoked: true }>("/auth/password/reset", {
      token,
      newPassword,
    }),
  confirmEmail: (token: string) =>
    post<{ verified: true }>("/auth/email/verification/confirm", { token }),
  requestEmailVerification: () =>
    post<{ message: string }>("/auth/email/verification/request"),
  me: () => get<AuthUser>("/auth/me"),
  logout: (refreshToken: string | null) =>
    post<{ message: string }>("/auth/logout", refreshToken ? { refreshToken } : {}),
};

type ConversationListResponse =
  | ChatConversation[]
  | {
      data?: ChatConversation[];
      hasMore?: boolean;
    };

type MessageListResponse =
  | ChatMessage[]
  | {
      data?: ChatMessage[];
      hasMore?: boolean;
      nextCursor?: string | null;
    };

type CursorListResponse<T> =
  | T[]
  | {
      data?: T[];
      items?: T[];
      hasMore?: boolean;
      nextCursor?: string | null;
    };

export const chatApi = {
  async listConversations(limit = 30) {
    const result = await get<ConversationListResponse>("/conversations", { page: 1, limit });
    const rows = Array.isArray(result) ? result : result.data ?? [];
    return rows.map(normalizeConversation);
  },
  async getMessages(conversationId: string, limit = 40) {
    const result = await get<MessageListResponse>(`/conversations/${conversationId}/messages`, { limit });
    const rows = Array.isArray(result) ? result : result.data ?? [];
    return rows.map(normalizeMessage);
  },
  async sendMessage(conversationId: string, text: string) {
    const result = await post<ChatMessage>(`/conversations/${conversationId}/messages`, {
      type: "text",
      text,
    });
    return normalizeMessage(result);
  },
  async uploadMedia(conversationId: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    const result = await post<ChatMessage>(`/conversations/${conversationId}/messages/media`, form);
    return normalizeMessage(result);
  },
  startConversation: (targetUserId: string, initialMessage?: string) =>
    post<ChatConversation>("/conversations", {
      participantIds: [targetUserId],
      type: "direct",
      initialMessage,
    }).then(normalizeConversation),
  markRead: (conversationId: string) => post<{ ok?: boolean }>(`/conversations/${conversationId}/read`),
  archive: (conversationId: string, archived: boolean) =>
    patch<{ ok?: boolean }>(`/conversations/${conversationId}/${archived ? "archive" : "unarchive"}`),
  deleteConversation: (conversationId: string) => del<{ ok?: boolean }>(`/conversations/${conversationId}`),
  deleteMessage: (conversationId: string, messageId: string) =>
    del<{ deleted?: boolean }>(`/conversations/${conversationId}/messages/${messageId}`),
  reportMessage: (conversationId: string, messageId: string, reason = "spam", note?: string) =>
    post<{ reported?: boolean; reportId?: string }>(`/conversations/${conversationId}/messages/${messageId}/report`, {
      reason,
      note: note?.trim() || undefined,
    }),
  blockUser: (userId: string, reason?: string) =>
    post<{ blocked?: boolean }>(`/conversations/blocks/${userId}`, {
      reason: reason?.trim() || undefined,
    }),
  unblockUser: (userId: string) => del<{ blocked?: boolean }>(`/conversations/blocks/${userId}`),
  blockStatus: (userId: string) => get<{ blocked?: boolean }>(`/conversations/blocks/${userId}`),
  async blockedUsers() {
    return (await listRows<SocialUserCard>("/conversations/blocks")).map(normalizeSocialUser);
  },
};

export const marketApi = {
  async config() {
    try {
      const remote = await get<Partial<MarketConfig>>("/market-config/me");
      return {
        ...fallbackMarket,
        ...remote,
        countryCode: remote.countryCode ?? fallbackMarket.countryCode,
        countryName: remote.countryName ?? fallbackMarket.countryName,
        currency: remote.currency ?? fallbackMarket.currency,
        locale: remote.locale ?? fallbackMarket.locale,
        regionLabel: remote.regionLabel ?? fallbackMarket.regionLabel,
        regionLabelPlural: remote.regionLabelPlural ?? fallbackMarket.regionLabelPlural,
        paymentLabel: remote.paymentLabel ?? fallbackMarket.paymentLabel,
      };
    } catch {
      return fallbackMarket;
    }
  },
};

export const taxonomyApi = {
  async search(query: string, module: string, limit = 8) {
    const q = query.trim();
    if (!q) return [];
    try {
      return await get<TaxonomySearchResult[]>("/taxonomy/search", { q, module, limit });
    } catch {
      return searchFallbackTaxonomy(q, module, limit);
    }
  },
  async dynamicForm(module: string, taxonomyNodeId: string, taxonomyPathSnapshot?: string) {
    try {
      return await get<DynamicFormResponse>(`/dynamic-forms/${module}/${taxonomyNodeId}`);
    } catch {
      const fallback = demoDynamicForms.produce;
      return {
        module,
        taxonomyNodeId,
        taxonomyPathSnapshot: taxonomyPathSnapshot ?? "",
        attributes: fallback.attributes.map((attribute) => ({
          ...attribute,
          taxonomyNodeId,
          attributeType: attribute.attributeType as DynamicFormResponse["attributes"][number]["attributeType"],
        })),
        suggestions: fallback.suggestions,
      };
    }
  },
};

export type CreateListingInput = {
  title: string;
  description?: string;
  category?: string;
  subCategory?: string;
  pricePerUnit: number;
  currency?: string;
  unit: string;
  quantityAvailable: number;
  qualityGrade?: string;
  harvestDate?: string;
  expiryDate?: string;
  images?: string[];
  latitude?: number;
  longitude?: number;
  locationName?: string;
  contactPhone?: string;
  negotiable?: boolean;
  taxonomyNodeId?: string;
  taxonomyPathSnapshot?: string;
  structuredAttributes?: StructuredAttributeValues;
  countryIso2?: string;
  currencyCode?: string;
  locale?: string;
  cityTown?: string;
  locationSnapshot?: Record<string, unknown>;
};

export type CreateDemandPostInput = {
  buyerName: string;
  buyerType: string;
  cropName: string;
  quantityKg: number;
  /** Max price per unit in `currencyCode` (backend maps to the legacy KES column for now). */
  maxPrice: number;
  deliveryArea: string;
  grade?: string;
  deliveryDate?: string;
  recurring?: boolean;
  recurrenceCadence?: string;
  certifications?: string[];
  notes?: string;
  latitude?: number;
  longitude?: number;
  taxonomyNodeId?: string;
  taxonomyPathSnapshot?: string;
  structuredAttributes?: StructuredAttributeValues;
  countryIso2?: string;
  currencyCode?: string;
  locale?: string;
  cityTown?: string;
  locationSnapshot?: Record<string, unknown>;
};

export type UpdateUserProfileInput = {
  displayName?: string;
  username?: string;
  phoneNumber?: string;
  bio?: string;
  county?: string;
  subCounty?: string;
  countryCode?: string;
  countryIso2?: string;
  countryIso3?: string;
  admin1Id?: string;
  admin2Id?: string;
  admin3Id?: string;
  admin4Name?: string;
  cityTown?: string;
  addressText?: string;
  locale?: string;
  currencyCode?: string;
  locationSnapshot?: Record<string, unknown>;
  farmSizeAcres?: number;
  crops?: string[];
  livestock?: string[];
  vehicleType?: string;
  vehicleCapacityTons?: number;
  specialization?: string;
  storageCapacityTons?: number;
  storageType?: string;
  categories?: string[];
  subCategories?: string[];
};

export type UpdateUserRolesInput = {
  roles: string[];
  primaryRole?: string;
};

export type CreateFarmActivityInput = {
  activityType: FarmActivityType;
  title: string;
  notes?: string;
  dueDate?: string;
  amountKes?: number;
  quantity?: number;
  unit?: string;
  done?: boolean;
};

export type UpdateListingInput = Partial<CreateListingInput> & {
  status?: "active" | "sold" | "expired" | "draft";
};

export type UpdateDemandPostInput = Partial<CreateDemandPostInput> & {
  status?: "open" | "closed" | "fulfilled";
};

export type CreateDemandResponseInput = {
  message?: string;
  /** Offered price per unit in the post's currency (currency-generic alias). */
  offeredPrice?: number;
  /** Quantity the supplier can provide (unit-generic alias). */
  availableQuantity?: number;
};

export const marketplaceApi = {
  createListing: (dto: CreateListingInput) => post<{ id: string }>("/marketplace/listings", dto),
  async uploadListingPhoto(file: File) {
    const form = new FormData();
    form.append("file", file);
    return post<ListingPhotoUpload>("/marketplace/listings/media", form);
  },
  async myListings() {
    const rows = await get<Record<string, unknown>[]>("/marketplace/listings/me");
    return (rows ?? []).map(normalizeMyListing);
  },
  updateListing: (listingId: string, dto: UpdateListingInput) =>
    patch<Record<string, unknown>>(`/marketplace/listings/${listingId}`, dto).then(normalizeMyListing),
  deleteListing: (listingId: string) => del<{ deleted?: boolean }>(`/marketplace/listings/${listingId}`),
};

export const demandApi = {
  createPost: (dto: CreateDemandPostInput) => post<{ id: string }>("/demand-posts", dto),
  async mine() {
    const rows = await get<Record<string, unknown>[]>("/demand-posts/mine");
    return (rows ?? []).map(normalizeDemandPostRecord);
  },
  detail: (postId: string) =>
    get<Record<string, unknown>>(`/demand-posts/${postId}`).then(normalizeDemandPostRecord),
  update: (postId: string, dto: UpdateDemandPostInput) =>
    patch<Record<string, unknown>>(`/demand-posts/${postId}`, dto).then(normalizeDemandPostRecord),
  close: (postId: string) =>
    patch<Record<string, unknown>>(`/demand-posts/${postId}`, { status: "closed" }).then(normalizeDemandPostRecord),
  reopen: (postId: string) =>
    patch<Record<string, unknown>>(`/demand-posts/${postId}`, { status: "open" }).then(normalizeDemandPostRecord),
  remove: (postId: string) => del<{ deleted?: boolean }>(`/demand-posts/${postId}`),
  respond: (postId: string, dto: CreateDemandResponseInput) =>
    // Both API generations accept these storage-field names. Their values
    // remain in the demand's currency/unit; do not convert them to KES/kg.
    post<Record<string, unknown>>(`/demand-posts/${postId}/responses`, {
      message: dto.message,
      offeredPriceKes: dto.offeredPrice,
      availableKg: dto.availableQuantity,
    }).then(normalizeDemandResponse),
  async responses(postId: string) {
    const rows = await get<Record<string, unknown>[]>(`/demand-posts/${postId}/responses`);
    return (rows ?? []).map(normalizeDemandResponse);
  },
  acceptResponse: (postId: string, responseId: string, reason?: string) =>
    patch<Record<string, unknown>>(`/demand-posts/${postId}/responses/${responseId}/accept`, {
      reason: reason?.trim() || undefined,
    }).then(normalizeDemandResponse),
  declineResponse: (postId: string, responseId: string, reason?: string) =>
    patch<Record<string, unknown>>(`/demand-posts/${postId}/responses/${responseId}/decline`, {
      reason: reason?.trim() || undefined,
    }).then(normalizeDemandResponse),
};

export type CreateTransportRequestInput = {
  providerId?: string;
  pickup: string;
  dropoff: string;
  cargoType: string;
  weightTons: number;
  vehicleType: string;
  scheduleLater?: boolean;
  scheduledAt?: string;
  countryIso2?: string;
  admin1Id?: string;
  admin2Id?: string;
  cityTown?: string;
  locale?: string;
  currencyCode?: string;
  locationSnapshot?: Record<string, unknown>;
};

export type CreateServiceRequestInput = {
  message?: string;
  productId?: string;
  productName?: string;
  payload?: Record<string, unknown>;
  countryIso2?: string;
  admin1Id?: string;
  admin2Id?: string;
  cityTown?: string;
  locale?: string;
  currencyCode?: string;
  locationSnapshot?: Record<string, unknown>;
};

export const servicesApi = {
  createTransportRequest: (dto: CreateTransportRequestInput) =>
    post<Record<string, unknown>>("/services/transport/requests", dto).then(normalizeServiceRequest),
  createStorageReservation: (providerId: string, dto: CreateServiceRequestInput) =>
    post<Record<string, unknown>>(`/services/storage/${providerId}/reservations`, dto).then(normalizeServiceRequest),
  createProcessingRequest: (providerId: string, dto: CreateServiceRequestInput) =>
    post<Record<string, unknown>>(`/services/processing/${providerId}/requests`, dto).then(normalizeServiceRequest),
  createAgrovetEnquiry: (providerId: string, dto: CreateServiceRequestInput) =>
    post<Record<string, unknown>>(`/services/agrovets/${providerId}/enquiries`, dto).then(normalizeServiceRequest),
  async myRequests() {
    const rows = await listRows<Record<string, unknown>>("/services/requests/mine");
    return rows.map(normalizeServiceRequest);
  },
  requestDetail: (requestId: string) =>
    get<Record<string, unknown>>(`/services/requests/${requestId}`).then(normalizeServiceRequest),
  cancelRequest: (requestId: string) =>
    patch<Record<string, unknown>>(`/services/requests/${requestId}/cancel`).then(normalizeServiceRequest),
  provider: (providerId: string) => get<Record<string, unknown>>(`/services/providers/${providerId}`),
};

export type CreateConsultationInput = {
  consultationType: "chat" | "video" | "farm_visit";
  scheduledAt?: string;
  slot?: string;
  durationMinutes?: number;
  phoneNumber?: string;
  message?: string;
};

export const consultationsApi = {
  book: (providerId: string, dto: CreateConsultationInput) =>
    post<Record<string, unknown>>(`/consultations/experts/${providerId}/bookings`, dto).then(normalizeConsultation),
  async mine() {
    const rows = await listRows<Record<string, unknown>>("/consultations/mine");
    return rows.map(normalizeConsultation);
  },
  detail: (consultationId: string) =>
    get<Record<string, unknown>>(`/consultations/${consultationId}`).then(normalizeConsultation),
  cancel: (consultationId: string) =>
    post<Record<string, unknown>>(`/consultations/${consultationId}/refund`).then(normalizeConsultation),
};

export const farmsApi = {
  listMine: () => get<FarmRecord[]>("/farms"),
  detail: (farmId: string) => get<FarmRecord>(`/farms/${farmId}`),
};

export const farmOpsApi = {
  dashboard: (farmId: string) => get<FarmDashboard>(`/farms/${farmId}/dashboard`),
  activities: (farmId: string, status: "open" | "done" | "overdue" | "today" | "week" | "all", limit = 50) =>
    get<FarmActivitiesEnvelope>(`/farms/${farmId}/activities`, { status, limit, offset: 0 }),
  createActivity: (farmId: string, dto: CreateFarmActivityInput) =>
    post<FarmActivity>(`/farms/${farmId}/activities`, dto),
  markDone: (activityId: string, dto?: { amountKes?: number; quantity?: number; unit?: string; notes?: string }) =>
    patch<FarmActivity>(`/farm-activities/${activityId}/done`, dto ?? {}),
  deleteActivity: (activityId: string) => del<{ deleted?: boolean }>(`/farm-activities/${activityId}`),
};

type CommunityListResponse =
  | Community[]
  | {
      data?: Community[];
      total?: number;
    };

type PostMediaUpload = {
  url: string;
  objectKey?: string;
  mediaType: "image" | "video" | string;
  contentType?: string;
  sizeBytes?: number;
};

type PostVideoUpload = {
  uploadUrl: string;
  cloudflareUid: string;
  expiresAt?: string;
  jobId: string;
};

type PostMediaJob = {
  jobId: string;
  cloudflareUid: string;
  status: string;
  videoUrl?: string | null;
  hlsUrl?: string | null;
  videoThumbnailUrl?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
};

export const communityApi = {
  async list(params?: { q?: string; category?: string; communityType?: string; page?: number; limit?: number }) {
    const result = await get<CommunityListResponse>("/communities", {
      page: params?.page ?? 1,
      limit: params?.limit ?? 24,
      q: params?.q?.trim() || undefined,
      category: params?.category || undefined,
      communityType: params?.communityType || undefined,
    });
    const rows = Array.isArray(result) ? result : result.data ?? [];
    return rows.map(normalizeCommunity);
  },
  detail: (communityId: string) => get<Community>(`/communities/${communityId}`).then(normalizeCommunity),
  myCommunities: () => get<Community[]>("/communities/me").then((rows) => rows.map(normalizeCommunity)),
  join: (communityId: string) => post<CommunityMember>(`/communities/${communityId}/join`).then(normalizeCommunityMember),
  leave: (communityId: string) => del<void>(`/communities/${communityId}/leave`),
  members: (communityId: string) =>
    get<CommunityMember[]>(`/communities/${communityId}/members`).then((rows) => rows.map(normalizeCommunityMember)),
  async posts(communityId: string, limit = 20) {
    const rows = await listRows<PostItem>(`/communities/${communityId}/posts`, { limit });
    return rows.map(normalizePost);
  },
};

export const postsApi = {
  async create(dto: {
    text?: string;
    imageUrls?: string[];
    videoUrl?: string;
    videoThumbnailUrl?: string;
    cloudflareVideoUid?: string;
    communityId?: string;
    hashtags?: string[];
  }) {
    const result = await post<PostItem>("/posts", dto);
    return normalizePost(result);
  },
  async uploadMedia(file: File) {
    const form = new FormData();
    form.append("file", file);
    return post<PostMediaUpload>("/posts/media", form);
  },
  createVideoUploadUrl: (dto?: { filename?: string; thumbnailUrl?: string }) =>
    post<PostVideoUpload>("/posts/video-upload-url", dto ?? {}),
  async devVideoUpload(cloudflareUid: string, file: File) {
    const form = new FormData();
    form.append("file", file);
    return post<PostMediaJob>(`/posts/dev-video-upload/${cloudflareUid}`, form);
  },
  mediaJobStatus: (jobId: string) => get<PostMediaJob>(`/posts/media-jobs/${jobId}`),
  detail: (postId: string) => get<PostItem & { _id?: string }>(`/posts/${postId}`).then((raw) =>
    normalizePost({ ...raw, id: raw.id || raw._id || postId })),
  like: (postId: string) => post<{ liked?: boolean }>(`/posts/${postId}/like`),
  unlike: (postId: string) => del<{ liked?: boolean }>(`/posts/${postId}/like`),
  remove: (postId: string) => del<{ deleted?: boolean }>(`/posts/${postId}`),
  async comments(postId: string, limit = 30, page = 1) {
    const rows = await listRows<Record<string, unknown>>(`/posts/${postId}/comments`, { limit, page });
    return rows.map(normalizeComment);
  },
  addComment: (postId: string, text: string, parentCommentId?: string) =>
    post<Record<string, unknown>>(`/posts/${postId}/comments`, {
      text: text.trim(),
      parentCommentId,
    }).then(normalizeComment),
};

export const feedApi = {
  async forYou(limit = 12): Promise<SocialPost[]> {
    const result = await get<CursorListResponse<PostItem>>("/feed/for-you", { limit });
    const rows = Array.isArray(result) ? result : result.data ?? result.items ?? [];
    return rows.map((row) => {
      const post = normalizePost(row);
      return {
        id: post.id,
        authorId: post.authorId,
        authorDisplayName: post.authorDisplayName,
        authorAvatarUrl: post.authorAvatarUrl,
        authorRole: post.authorRole,
        text: post.text,
        imageUrls: post.imageUrls,
        videoUrl: post.videoUrl,
        videoThumbnailUrl: post.videoThumbnailUrl,
        hashtags: post.hashtags,
        locationName: post.locationName,
        likesCount: post.likesCount,
        commentsCount: post.commentsCount,
        sharesCount: post.sharesCount,
        createdAt: post.createdAt,
        isSponsored: post.isSponsored,
        campaignId: post.campaignId,
        sponsorName: post.sponsorName,
        sponsoredLabel: post.sponsoredLabel,
        sponsoredCtaLabel: post.sponsoredCtaLabel,
        sponsoredProductId: post.sponsoredProductId,
        sponsoredProductName: post.sponsoredProductName,
        clickToken: post.clickToken,
      };
    });
  },
};

export const followApi = {
  follow: (userId: string) => post<{ following?: boolean }>(`/users/${userId}/follow`),
  unfollow: (userId: string) => del<{ following?: boolean }>(`/users/${userId}/follow`),
  /** JWT-guarded id lookup — used to read viewer-specific isFollowing state. */
  publicProfile: (userId: string) =>
    get<{ id: string; username?: string | null; isFollowing?: boolean }>(`/users/${userId}`),
};

export const usersApi = {
  me: () => get<AuthUser>("/users/me"),
  myStats: () => get<ProfileStats>("/users/me/stats"),
  async myPosts(userId: string, limit = 20) {
    return (await listRows<PostItem>(`/users/${userId}/posts`, { limit })).map(normalizePost);
  },
  async myFollowers(limit = 30) {
    return (await listRows<SocialUserCard>("/users/me/followers", { page: 1, limit })).map(normalizeSocialUser);
  },
  async myFollowing(limit = 30) {
    return (await listRows<SocialUserCard>("/users/me/following", { page: 1, limit })).map(normalizeSocialUser);
  },
  async myPostLikes(limit = 20) {
    const response = await get<CursorListResponse<PostLikeActivity>>("/users/me/post-likes", { limit });
    const rows = Array.isArray(response) ? response : response.data ?? response.items ?? [];
    return rows.map((row) => ({
      ...row,
      likers: (row.likers ?? []).map(normalizeSocialUser),
    }));
  },
  updateMe: (dto: UpdateUserProfileInput) => patch<AuthUser>("/users/me", dto),
  updateRoles: (dto: UpdateUserRolesInput) => patch<AuthUser>("/users/me/roles", dto),
  privacySettings: () => get<UserPrivacySettings>("/users/me/privacy-settings"),
  updatePrivacySettings: (dto: UserPrivacySettings) => patch<UserPrivacySettings>("/users/me/privacy-settings", dto),
  notificationPreferences: () => get<UserNotificationPreferences>("/users/me/notification-preferences"),
  updateNotificationPreferences: (dto: UserNotificationPreferences) =>
    patch<UserNotificationPreferences>("/users/me/notification-preferences", dto),
  createSupportTicket: (dto: {
    topic: string;
    subject: string;
    message: string;
    metadata?: Record<string, unknown>;
  }) => post<{ id?: string; message?: string }>("/users/me/support-tickets", dto),
  async uploadAvatar(file: File) {
    const form = new FormData();
    form.append("file", file);
    return post<{ avatarUrl: string }>("/users/me/avatar", form);
  },
  async uploadCover(file: File) {
    const form = new FormData();
    form.append("file", file);
    return post<{ coverUrl: string }>("/users/me/cover", form);
  },
  checkUsername: (username: string) =>
    get<{ username: string; available: boolean; message?: string }>("/users/username/check", {
      username: username.trim(),
    }),
  search: (query: string, limit = 8) =>
    get<UserSearchResult[]>("/users/search", {
      q: query.trim(),
      limit,
    }),
};

export const locationApi = {
  countries: (params?: { launchOnly?: boolean; region?: string; subregion?: string; q?: string }) =>
    get<LocationCountry[]>("/location/countries", params),
  country: (iso2: string) => get<LocationCountry & { adminLevels?: LocationAdminLevel[] }>(`/location/countries/${iso2}`),
  adminLevels: (iso2: string) => get<LocationAdminLevel[]>(`/location/countries/${iso2}/admin-levels`),
  adminUnits: (iso2: string, params: { level: number; parentId?: string }) =>
    get<LocationAdminUnit[]>(`/location/countries/${iso2}/admin-units`, params),
};

export const liveApi = {
  joinToken: (sessionId: string) =>
    post<{
      livekitWsUrl?: string;
      livekit_ws_url?: string;
      token: string;
      roomName?: string;
      room_name?: string;
      userRole?: string;
      user_role?: string;
    }>(`/live-sessions/${sessionId}/join-token`),
  async comments(sessionId: string, limit = 30) {
    return listRows<LiveComment>(`/live-sessions/${sessionId}/comments`, { limit });
  },
  createComment: (sessionId: string, commentText: string) =>
    post<LiveComment>(`/live-sessions/${sessionId}/comments`, { commentText }),
  deleteComment: (sessionId: string, commentId: string) =>
    del<LiveComment | { ok?: boolean }>(`/live-sessions/${sessionId}/comments/${commentId}`),
  pinComment: (sessionId: string, commentId: string) =>
    post<LiveComment>(`/live-sessions/${sessionId}/comments/${commentId}/pin`),
  react: (sessionId: string, reactionType: LiveReaction["reactionType"]) =>
    post<LiveReaction>(`/live-sessions/${sessionId}/reactions`, { reactionType }),
  async questions(sessionId: string, limit = 20) {
    return listRows<LiveQuestion>(`/live-sessions/${sessionId}/questions`, { limit });
  },
  createQuestion: (sessionId: string, questionText: string) =>
    post<LiveQuestion>(`/live-sessions/${sessionId}/questions`, { questionText }),
  pinQuestion: (sessionId: string, questionId: string) =>
    post<LiveQuestion>(`/live-sessions/${sessionId}/questions/${questionId}/pin`),
  markQuestionAnswered: (sessionId: string, questionId: string) =>
    patch<LiveQuestion>(`/live-sessions/${sessionId}/questions/${questionId}/answered`),
  async speakerRequests(sessionId: string, limit = 20) {
    return listRows<LiveSpeakerRequest>(`/live-sessions/${sessionId}/speaker-requests`, { limit });
  },
  requestToSpeak: (sessionId: string) =>
    post<LiveSpeakerRequest>(`/live-sessions/${sessionId}/speaker-requests`),
  approveSpeakerRequest: (sessionId: string, requestId: string) =>
    patch<LiveSpeakerRequest>(`/live-sessions/${sessionId}/speaker-requests/${requestId}/approve`),
  rejectSpeakerRequest: (sessionId: string, requestId: string, reason?: string) =>
    patch<LiveSpeakerRequest>(`/live-sessions/${sessionId}/speaker-requests/${requestId}/reject`, {
      reason: reason?.trim() || undefined,
    }),
  removeSpeaker: (sessionId: string, speakerUserId: string) =>
    post<{ ok?: boolean }>(`/live-sessions/${sessionId}/speakers/${speakerUserId}/remove`),
  async pinnedItems(sessionId: string, limit = 12) {
    return listRows<LivePinnedItem>(`/live-sessions/${sessionId}/products`, { limit });
  },
  recordPinnedItemClick: (sessionId: string, itemId: string) =>
    post<LivePinnedItem>(`/live-sessions/${sessionId}/products/${itemId}/click`),
  async participants(sessionId: string, limit = 40) {
    return listRows<LiveParticipant>(`/live-sessions/${sessionId}/participants`, { limit });
  },
  async moderationActions(sessionId: string, limit = 20) {
    return listRows<LiveModerationAction>(`/live-sessions/${sessionId}/moderation/actions`, { limit });
  },
  muteUser: (sessionId: string, targetUserId: string, reason?: string) =>
    post<LiveParticipant>(`/live-sessions/${sessionId}/moderation/mute-user/${targetUserId}`, moderationReason(reason)),
  unmuteUser: (sessionId: string, targetUserId: string, reason?: string) =>
    post<LiveParticipant>(`/live-sessions/${sessionId}/moderation/unmute-user/${targetUserId}`, moderationReason(reason)),
  removeUser: (sessionId: string, targetUserId: string, reason?: string) =>
    post<LiveParticipant>(`/live-sessions/${sessionId}/moderation/remove-user/${targetUserId}`, moderationReason(reason)),
  blockUser: (sessionId: string, targetUserId: string, reason?: string) =>
    post<LiveParticipant>(`/live-sessions/${sessionId}/moderation/block-user/${targetUserId}`, moderationReason(reason)),
  unblockUser: (sessionId: string, targetUserId: string, reason?: string) =>
    post<LiveParticipant>(`/live-sessions/${sessionId}/moderation/unblock-user/${targetUserId}`, moderationReason(reason)),
  disableComments: (sessionId: string, reason?: string) =>
    post<LiveModerationAction>(`/live-sessions/${sessionId}/moderation/comments/disable`, moderationReason(reason)),
  enableComments: (sessionId: string, reason?: string) =>
    post<LiveModerationAction>(`/live-sessions/${sessionId}/moderation/comments/enable`, moderationReason(reason)),
  recordings: (sessionId: string) => get<LiveRecording[]>(`/live-sessions/${sessionId}/recordings`),
  startRecording: (sessionId: string) => post<LiveRecording>(`/live-sessions/${sessionId}/recordings/start`),
  stopRecording: (sessionId: string) => post<LiveRecording>(`/live-sessions/${sessionId}/recordings/stop`),
  analytics: (sessionId: string) => get<LiveAnalytics>(`/live-sessions/${sessionId}/analytics`),
  access: (sessionId: string) => get<LiveAccess>(`/live-sessions/${sessionId}/access`),
  ticketStatus: (sessionId: string) => get<LiveTicketStatus>(`/live-sessions/${sessionId}/tickets/status`),
  initiateTicketPayment: (sessionId: string, phoneNumber: string) =>
    post<LiveTicketStatus>(`/live-sessions/${sessionId}/tickets/payment/initiate`, { phoneNumber }),
  async members(sessionId: string, limit = 40) {
    return listRows<LiveMember>(`/live-sessions/${sessionId}/members`, { limit });
  },
  addMember: (sessionId: string, userId: string) =>
    post<LiveMember>(`/live-sessions/${sessionId}/members`, { userId }),
  removeMember: (sessionId: string, memberUserId: string) =>
    del<LiveMember>(`/live-sessions/${sessionId}/members/${memberUserId}`),
};

async function listRows<T>(url: string, params?: Record<string, unknown>) {
  const result = await get<CursorListResponse<T>>(url, params);
  if (Array.isArray(result)) return result;
  return result.data ?? result.items ?? [];
}

function moderationReason(reason?: string) {
  return { reason: reason?.trim() || "Updated from web live room controls." };
}

function searchFallbackTaxonomy(query: string, module: string, limit: number) {
  const q = query.toLowerCase();
  return demoTaxonomyResults
    .filter((item) => {
      const moduleMatches = !module || item.moduleScope?.includes(module);
      const haystack = [item.name, item.taxonomyPath, item.slug, item.matchedAlias]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return moduleMatches && haystack.includes(q);
    })
    .slice(0, limit);
}

function normalizeConversation(raw: ChatConversation & { _id?: string; id?: string }): ChatConversation {
  const id = raw.id ?? raw._id ?? "";
  return {
    ...raw,
    id,
    participantIds: raw.participantIds ?? [],
    type: raw.type ?? "direct",
    unreadCount: raw.unreadCount ?? 0,
    isArchived: raw.isArchived ?? false,
  };
}

function normalizeMessage(raw: ChatMessage & { _id?: string; id?: string }): ChatMessage {
  return {
    ...raw,
    id: raw.id ?? raw._id ?? "",
    type: raw.type ?? "text",
    isDeleted: raw.isDeleted ?? false,
  };
}

function normalizeCommunity(raw: Community & { _id?: string; id?: string }): Community {
  return {
    ...raw,
    id: raw.id ?? raw._id ?? "",
    communityType: raw.communityType ?? "open",
    tags: raw.tags ?? [],
    memberCount: raw.memberCount ?? 0,
    postCount: raw.postCount ?? 0,
    isFeatured: raw.isFeatured ?? false,
  };
}

function normalizeCommunityMember(raw: CommunityMember & { _id?: string; id?: string }): CommunityMember {
  return {
    ...raw,
    id: raw.id ?? raw._id ?? "",
    memberRole: raw.memberRole ?? "member",
    memberStatus: raw.memberStatus ?? "active",
  };
}

function normalizePost(raw: PostItem & { _id?: string; id?: string }): PostItem {
  return {
    ...raw,
    id: raw.id ?? raw._id ?? "",
    authorDisplayName: raw.authorDisplayName ?? "Skylight User",
    imageUrls: raw.imageUrls ?? [],
    hashtags: raw.hashtags ?? [],
    likesCount: raw.likesCount ?? 0,
    commentsCount: raw.commentsCount ?? 0,
    sharesCount: raw.sharesCount ?? 0,
    postType: raw.postType ?? "feed",
  };
}

function normalizeSocialUser(raw: SocialUserCard): SocialUserCard {
  return {
    ...raw,
    id: raw.id ?? "",
    displayName: raw.displayName ?? null,
    username: raw.username ?? null,
    avatarUrl: raw.avatarUrl ?? null,
    primaryRole: raw.primaryRole ?? null,
    roles: raw.roles ?? [],
    isFollowing: raw.isFollowing === true,
  };
}

function normalizeComment(raw: Record<string, unknown> & { _id?: unknown }): PostComment {
  return {
    id: str(raw.id) ?? str(raw._id) ?? "",
    postId: str(raw.postId) ?? "",
    authorId: str(raw.authorId) ?? "",
    authorDisplayName: str(raw.authorDisplayName) ?? "Skylight User",
    authorAvatarUrl: str(raw.authorAvatarUrl),
    text: str(raw.text) ?? "",
    likesCount: num(raw.likesCount) ?? 0,
    createdAt: str(raw.createdAt),
  };
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

function num(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeMyListing(raw: Record<string, unknown>): MyListing {
  const images = Array.isArray(raw.images) ? (raw.images as unknown[]).filter((x) => typeof x === "string") : [];
  return {
    id: str(raw.id) ?? "",
    title: str(raw.title) ?? "Untitled listing",
    status: str(raw.status) ?? "active",
    price: num(raw.pricePerUnit) ?? 0,
    currency: str(raw.currency) ?? fallbackMarket.currency,
    unit: str(raw.unit) ?? "unit",
    quantity: num(raw.quantityAvailable) ?? 0,
    imageUrl: (images[0] as string | undefined) ?? null,
    category: str(raw.categoryName) ?? str(raw.category),
    taxonomyPath: str(raw.taxonomyPathSnapshot),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function normalizeDemandPostRecord(raw: Record<string, unknown>): DemandPost {
  const snapshot = (raw.locationSnapshot ?? {}) as Record<string, unknown>;
  return {
    id: str(raw.id) ?? "",
    title: str(raw.cropName) ?? "Produce need",
    taxonomyPath: str(raw.taxonomyPathSnapshot) ?? "",
    buyerName: str(raw.buyerName) ?? "Buyer",
    buyerType: str(raw.buyerType) ?? "",
    quantity: num(raw.quantityKg) ?? 0,
    unit: "kg",
    maxPrice: num(raw.maxPriceKes) ?? 0,
    currency: str(raw.currencyCode) ?? fallbackMarket.currency,
    location: str(raw.deliveryArea) ?? str(raw.cityTown) ?? "",
    region:
      str(snapshot.admin1Name) ??
      str(snapshot.county) ??
      str(raw.cityTown) ??
      str(raw.deliveryArea) ??
      "",
    neededBy: str(raw.deliveryDate) ?? "",
    responses: num(raw.responseCount) ?? 0,
    buyerUserId: str(raw.buyerUserId),
    status: str(raw.status) ?? "open",
    grade: str(raw.grade),
    notes: str(raw.notes),
    recurring: raw.recurring === true,
    certifications: Array.isArray(raw.certifications)
      ? (raw.certifications as unknown[]).filter((x): x is string => typeof x === "string")
      : [],
    postedAt: str(raw.postedAt),
  };
}

function normalizeDemandResponse(raw: Record<string, unknown>): DemandResponse {
  return {
    id: str(raw.id) ?? "",
    demandPostId: str(raw.demandPostId) ?? "",
    responderUserId: str(raw.responderUserId) ?? "",
    message: str(raw.message),
    offeredPrice: num(raw.offeredPriceKes),
    availableQuantity: num(raw.availableKg),
    status: str(raw.status) ?? "pending",
    createdAt: str(raw.createdAt),
  };
}

function normalizeServiceRequest(raw: Record<string, unknown>): ServiceRequest {
  return {
    id: str(raw.id) ?? "",
    requestType: str(raw.requestType) ?? "transport",
    status: str(raw.status) ?? "pending",
    providerId: str(raw.providerId),
    providerName: str(raw.providerName),
    providerType: str(raw.providerType),
    requesterId: str(raw.requesterId) ?? "",
    requesterName: str(raw.requesterName),
    payload: (raw.payload ?? {}) as Record<string, unknown>,
    estimate: num(raw.estimate),
    pickupLabel: str(raw.pickupLabel),
    dropoffLabel: str(raw.dropoffLabel),
    cargoLabel: str(raw.cargoLabel),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function normalizeConsultation(raw: Record<string, unknown>): Consultation {
  return {
    id: str(raw.id) ?? "",
    requesterId: str(raw.requesterId) ?? "",
    expertUserId: str(raw.expertUserId) ?? "",
    providerId: str(raw.providerId) ?? "",
    consultationType: str(raw.consultationType) ?? "chat",
    status: str(raw.status) ?? "requested",
    scheduledAt: str(raw.scheduledAt),
    slot: str(raw.slot),
    durationMinutes: num(raw.durationMinutes) ?? 30,
    message: str(raw.message),
    amount: num(raw.amount) ?? 0,
    currency: str(raw.currency) ?? fallbackMarket.currency,
    acceptedAt: str(raw.acceptedAt),
    rejectedAt: str(raw.rejectedAt),
    rejectedReason: str(raw.rejectedReason),
    createdAt: str(raw.createdAt),
  };
}

// ── Contextual chat (chat-first conversion helper) ──────────────────────────

export type ChatContextType =
  | "listing"
  | "provider"
  | "expert"
  | "demand"
  | "request"
  | "consultation"
  | "campaign";

const CONTEXT_MESSAGES: Record<ChatContextType, (title: string) => string> = {
  listing: (title) => `Hi, I'm interested in your listing "${title}" on Skylight. Is it still available?`,
  provider: (title) => `Hi, I found "${title}" on Skylight and would like to ask about your services.`,
  expert: (title) => `Hi ${title}, I found your expert profile on Skylight and would like to consult you.`,
  demand: (title) => `Hi, I responded to your buyer need "${title}" on Skylight. I can supply — let's talk.`,
  request: (title) => `Hi, I just sent a service request${title ? ` for "${title}"` : ""} on Skylight.`,
  consultation: (title) => `Hi, I just requested a consultation${title ? ` about "${title}"` : ""} on Skylight.`,
  campaign: (title) => `Hi, I saw your sponsored post about "${title}" on Skylight and would like to know more.`,
};

/**
 * Chat-first CTA helper: start (or reuse) a direct conversation with a
 * templated context message and return the /messages deep link. Every
 * listing/provider/expert/demand CTA should route through this so context
 * params stay consistent.
 */
export async function startContextualChat(input: {
  targetUserId: string;
  contextType: ChatContextType;
  contextTitle: string;
  contextId?: string;
  message?: string;
}): Promise<{ conversation: ChatConversation; href: string }> {
  const intro = input.message?.trim() || CONTEXT_MESSAGES[input.contextType](input.contextTitle);
  const conversation = await chatApi.startConversation(input.targetUserId, intro);
  const params = new URLSearchParams({ conversationId: conversation.id });
  if (input.contextId) params.set(`${input.contextType}Id`, input.contextId);
  return { conversation, href: `/messages?${params.toString()}` };
}
